import NextLink from 'next/link'
import { styled, theme } from '../../../../stitches.config'
import * as React from 'react'
import Image from 'next/image'
import { Text } from '../Text/Text'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF'
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter'
import { faLinkedinIn } from '@fortawesome/free-brands-svg-icons/faLinkedinIn'
import { faInstagram } from '@fortawesome/free-brands-svg-icons/faInstagram'
import { faYoutube } from '@fortawesome/free-brands-svg-icons/faYoutube'
import StarRating from '../../marketing/reviews/StarRating'
import {
    SALES_PHONE_NUMBER_FORMATTED,
    SUPPORT_PHONE_NUMBER_FORMATTED,
    SALES_PHONE_NUMBER_TEL,
    SUPPORT_PHONE_NUMBER_TEL,
    SALES_PHONE_NUMBER_DATA_ID,
} from '../../marketing/text/PhoneNumbers/strings'

const FooterWrapper = styled('footer', {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
})
const FooterTop = styled('div', {
    backgroundColor: '$darkest',
    color: '$white',
    py: '2.55rem',
    px: '1rem',
    width: '100%',
})
const SectionList = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    '@md': {
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        gap: '0',
    },
})
const SectionListItem = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    listStyle: 'none',
    '@md': {
        justifyContent: 'normal',
        padding: '0 2rem',
        '&:last-child': {
            borderRight: 'none',
        },
    },
})
const SectionListItemContent = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
})
const LinkHeader = styled('p', {
    color: '$white',
    fontSize: '$4',
    marginTop: '8px',
    marginBottom: '10px',
})
const StyledLink = styled('a', {
    fontSize: '$2',
    '&:hover': {
        textDecoration: 'underline',
    },
})
const FooterBottom = styled('div', {
    backgroundColor: '$white',
    padding: '1rem',
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    '@md': {
        flexDirection: 'row',
    },
})
const FooterBottomLinks = styled('div', {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.5rem',
    fontSize: '$2',
    '@md': {
        gap: '.75rem',
        width: '59%',
    },
})
const FooterBottomLink = styled('a', {
    color: '$darkest',
    '&:hover': {
        textDecoration: 'underline',
    },
    '&:before': {
        content: '|',
        mr: '.5rem',
        '@md': {
            mr: '.75rem',
        },
    },
})
const FooterBottomLogos = styled('div', {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '.75rem',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    width: '82%',
    mt: '1.25rem',
    '@md': {
        mt: 'unset',
        gap: '1rem',
        width: '30%',
        justifyContent: 'flex-end',
    },
})
const IconsWrapper = styled('div', {
    display: 'flex',
    mb: '.5rem',
    '& svg': {
        minWidth: '1rem',
        fontSize: '.8rem',
        mr: '.35rem',
        border: '1px solid white',
        p: '.3rem',
        br: '50%',
    },
})
const RatingWrapper = styled('span', {
    display: 'flex',
    gap: '.35rem',
})
const TermsAndConditionsText = styled('p', {
    margin: '0 1rem 1rem',
    fontSize: '$2',
    color: theme.colors.dark,
    '@sm': {
        margin: '0 4rem 1rem',
    },
})

const styles = {
    marginTop: {
        marginTop: '1.5rem',
    },
    grayText: {
        color: theme.colors.mediumGray,
        '&:hover': {
            textDecoration: 'none',
        },
    },
}
interface SocialButtonProps {
    icon: IconProp
    url: string
    name: string
}
const SocialIcon = ({ icon, url, name }: SocialButtonProps) => {
    return (
        <a
            href={url}
            rel="noreferrer noopener"
            target="_blank"
            aria-label={`${name} button`}
        >
            <FontAwesomeIcon icon={icon} />
        </a>
    )
}

const ImageWrapper = styled('div', {})

export const ConsumerFooter = () => {
    return (
        <FooterWrapper>
            <FooterTop>
                <SectionList>
                    <SectionListItem>
                        <SectionListItemContent>
                            <LinkHeader>PerfectGift, LLC</LinkHeader>
                            <Text size={2}>495 Mansfield Avenue</Text>
                            <Text size={2}>Pittsburgh, PA 15205</Text>
                            <StyledLink
                                data-phone={SALES_PHONE_NUMBER_DATA_ID}
                                href={SALES_PHONE_NUMBER_TEL}
                                css={{ textDecoration: 'underline' }}
                            >
                                {SALES_PHONE_NUMBER_FORMATTED}
                            </StyledLink>
                        </SectionListItemContent>
                        <SectionListItemContent style={styles.marginTop}>
                            <LinkHeader>Follow us</LinkHeader>
                            <IconsWrapper>
                                <SocialIcon
                                    icon={faFacebookF}
                                    url={
                                        'https://www.facebook.com/PerfectGiftCom'
                                    }
                                    name={'Facebook'}
                                />
                                <SocialIcon
                                    icon={faLinkedinIn}
                                    url={
                                        'https://www.linkedin.com/company/perfectgiftcom/'
                                    }
                                    name={'LinkedIn'}
                                />
                                <SocialIcon
                                    icon={faTwitter}
                                    url={'https://twitter.com/PerfectGiftcom'}
                                    name={'Twitter'}
                                />
                                <SocialIcon
                                    icon={faInstagram}
                                    url={
                                        'https://www.instagram.com/perfectgiftcom/'
                                    }
                                    name={'Instagram'}
                                />
                                <SocialIcon
                                    icon={faYoutube}
                                    url={
                                        'https://www.youtube.com/channel/UCYAuggt2bUIdUokfLiv9piA'
                                    }
                                    name={'Youtube'}
                                />
                            </IconsWrapper>
                            <RatingWrapper>
                                <NextLink href={'/reviews'} passHref>
                                    <StyledLink>Customer reviews</StyledLink>
                                </NextLink>
                                <StarRating
                                    ratingValue={100}
                                    fillColor={`${theme.colors.warning}`}
                                    emptyColor={''}
                                    starSize={16}
                                />
                            </RatingWrapper>
                            <NextLink href={'/corporate/testimonials'} passHref>
                                <StyledLink>
                                    Corporate client testimonials
                                </StyledLink>
                            </NextLink>
                        </SectionListItemContent>
                    </SectionListItem>
                    <SectionListItem>
                        <SectionListItemContent>
                            <LinkHeader>Gifting</LinkHeader>
                            <NextLink href={'/visa-gift-cards'} passHref>
                                <StyledLink>Visa Gift Cards</StyledLink>
                            </NextLink>
                            <StyledLink css={styles.grayText}>
                                The PerfectGift &mdash; coming soon!
                            </StyledLink>
                            <NextLink href={'/brands'} passHref>
                                <StyledLink>Brands</StyledLink>
                            </NextLink>
                            <NextLink
                                href={'/corporate/bulk-gift-cards'}
                                passHref
                            >
                                <StyledLink>Corporate</StyledLink>
                            </NextLink>
                            <StyledLink css={styles.grayText}>
                                Gift ideas &mdash; coming soon!
                            </StyledLink>
                        </SectionListItemContent>
                    </SectionListItem>
                    <SectionListItem>
                        <SectionListItemContent>
                            <LinkHeader>Company</LinkHeader>
                            <NextLink href={'/about-us'} passHref>
                                <StyledLink>About</StyledLink>
                            </NextLink>
                            <StyledLink css={styles.grayText}>
                                History &mdash; coming soon!
                            </StyledLink>
                            <NextLink href={'/press-media'} passHref>
                                <StyledLink>Press &amp; Media</StyledLink>
                            </NextLink>
                            <NextLink href={'/careers'} passHref>
                                <StyledLink>Careers</StyledLink>
                            </NextLink>
                            <NextLink href={'/pricing'} passHref>
                                <StyledLink>Consumer Pricing</StyledLink>
                            </NextLink>
                            <NextLink href={'/corporate/pricing'} passHref>
                                <StyledLink>Corporate Pricing</StyledLink>
                            </NextLink>
                        </SectionListItemContent>
                    </SectionListItem>
                    <SectionListItem>
                        <SectionListItemContent>
                            <LinkHeader>Partner with us</LinkHeader>
                            <NextLink href={'/corporate/api'} passHref>
                                <StyledLink>APIs</StyledLink>
                            </NextLink>
                            <StyledLink css={styles.grayText}>
                                Become a merchant &mdash; coming soon!
                            </StyledLink>
                            <StyledLink css={styles.grayText}>
                                Merchant portal &mdash; coming soon!
                            </StyledLink>
                            <NextLink
                                href={'/corporate/business-development'}
                                passHref
                            >
                                <StyledLink>Business development</StyledLink>
                            </NextLink>
                        </SectionListItemContent>
                    </SectionListItem>
                    <SectionListItem>
                        <SectionListItemContent>
                            <LinkHeader>24/7 Support</LinkHeader>
                            <StyledLink href={SUPPORT_PHONE_NUMBER_TEL}>
                                Call us:{' '}
                                <span style={{ textDecoration: 'underline' }}>
                                    {SUPPORT_PHONE_NUMBER_FORMATTED}
                                </span>
                            </StyledLink>
                            <NextLink href={'/activate'} passHref>
                                <StyledLink>Activate</StyledLink>
                            </NextLink>
                            <NextLink href={'/check-balance'} passHref>
                                <StyledLink>Check balance</StyledLink>
                            </NextLink>
                            <NextLink href={'/faq'} passHref>
                                <StyledLink>FAQs</StyledLink>
                            </NextLink>
                            <StyledLink
                                rel="noopener"
                                target="_blank"
                                href="https://perfectgiftsupport.zendesk.com/hc/en-us"
                            >
                                Help center
                            </StyledLink>
                        </SectionListItemContent>
                    </SectionListItem>
                </SectionList>
            </FooterTop>
            <FooterBottom>
                <FooterBottomLinks>
                    <div>
                        &copy;
                        {new Date().getFullYear()} - A{' '}
                        <StyledLink
                            rel="noopener"
                            target="_blank"
                            href="https://www.wolfe.com/"
                            style={{
                                fontWeight: 'bold',
                            }}
                        >
                            Wolfe, LLC
                        </StyledLink>{' '}
                        Company. All rights reserved
                    </div>
                    <NextLink href={'/privacy-policy'} passHref>
                        <FooterBottomLink>Privacy Policy</FooterBottomLink>
                    </NextLink>
                    <NextLink href={'/terms-of-use'} passHref>
                        <FooterBottomLink>Terms of Use</FooterBottomLink>
                    </NextLink>
                    <NextLink href={'/terms-of-service'} passHref>
                        <FooterBottomLink>Terms of Service</FooterBottomLink>
                    </NextLink>
                    <NextLink href={'/accessibility'} passHref>
                        <FooterBottomLink>
                            Accessibility Statement
                        </FooterBottomLink>
                    </NextLink>
                    <NextLink href={'/sitemap'} passHref>
                        <FooterBottomLink>Sitemap</FooterBottomLink>
                    </NextLink>
                    <NextLink href={'/cardholder-agreement'} passHref>
                        <FooterBottomLink>
                            Cardholder agreement
                        </FooterBottomLink>
                    </NextLink>
                </FooterBottomLinks>
                <FooterBottomLogos>
                    <ImageWrapper>
                        <Image
                            src={'/images/pci_compliant_badge.png'}
                            alt={'PCI Compliant Badge'}
                            width={78}
                            height={32}
                            layout={'fixed'}
                        />
                    </ImageWrapper>
                    <a
                        href="https://www.bbb.org/us/pa/pittsburgh/profile/gift-cards/perfect-gift-llc-0141-71066921"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        <Image
                            src="https://seal-westernpennsylvania.bbb.org/seals/blue-seal-120-61-bbb-71066921.png"
                            width="90"
                            height="46"
                            alt="Better Business Bureau Logo"
                        />
                    </a>
                    <ImageWrapper>
                        <Image
                            src={'/images/visa_logo.png'}
                            alt={'Visa logo'}
                            width={59}
                            height={19}
                            layout={'fixed'}
                        />
                    </ImageWrapper>
                    <ImageWrapper>
                        <Image
                            src={'/images/mc_logo.png'}
                            alt={'Mastercard logo'}
                            width={59}
                            height={19}
                            layout={'fixed'}
                        />
                    </ImageWrapper>
                    <ImageWrapper>
                        <Image
                            src={'/images/american-express_logo.png'}
                            alt={'American Express logo'}
                            width={59}
                            height={19}
                            layout={'fixed'}
                        />
                    </ImageWrapper>
                </FooterBottomLogos>
                {/* @TODO Add Norton badge once available  */}
            </FooterBottom>
            <TermsAndConditionsText>
                The PerfectGift Visa&reg; Reward Cards are issued by Sutton
                Bank&reg;, Member FDIC, pursuant to a license from Visa U.S.A.
                Inc. No cash access or recurring payments. Card can be used
                everywhere Visa debit cards are accepted. Visa Virtual Account
                can be redeemed at every Internet, mail order, and telephone
                merchant everywhere Visa debit cards are accepted. Terms,
                conditions and expiration apply. The PerfectGift Mastercard&reg;
                Reward Cards are issued by Sutton Bank&reg;, Member FDIC,
                pursuant to a license from Mastercard International
                Incorporated. No cash access or recurring payments. Card can be
                used everywhere Mastercard debit cards are accepted. Mastercard
                Virtual Account can be redeemed at every Internet, mail order,
                and telephone merchant everywhere Mastercard debit cards are
                accepted. Terms, conditions and expiration apply.
            </TermsAndConditionsText>
        </FooterWrapper>
    )
}
export default ConsumerFooter
